import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, X } from "lucide-react";

interface Props {
  windowId: string;
  startsAt: string;
  endsAt: string;
}

const formatWhen = (iso: string) =>
  new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
    timeZoneName: "short",
  }).format(new Date(iso));

const formatCountdown = (target: Date): string => {
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return "now";
  const totalMin = Math.floor(ms / 60_000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const storageKey = (id: string) => `maint-banner-dismissed:${id}`;

const AdminMaintenanceBanner = ({ windowId, startsAt, endsAt }: Props) => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey(windowId)) === "1";
    } catch {
      return false;
    }
  });
  const [, force] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => force((n) => n + 1), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey(windowId), "1");
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const countdown = formatCountdown(new Date(startsAt));

  return (
    <div className="sticky top-0 z-50 w-full bg-primary text-primary-foreground border-b border-primary/40 shadow-sm">
      <div className="container mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        <Wrench className="w-4 h-4 shrink-0" />
        <span className="flex-1 min-w-0">
          <strong>Maintenance scheduled</strong>{" "}
          <span className="opacity-90">
            starts in {countdown} ({formatWhen(startsAt)}), ends {formatWhen(endsAt)}.
          </span>{" "}
          <Link to="/admin/maintenance" className="underline font-medium hover:opacity-80">
            Manage
          </Link>
        </span>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="p-1 rounded hover:bg-primary-foreground/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AdminMaintenanceBanner;
